import { useEffect } from 'react';
import { isWeb } from '@helpers';

type EventHandlerEntry = {
  node: Document;
  handler: (event: Event) => void;
  capture: boolean;
};

type EventHandlersMap = Record<string, EventHandlerEntry[]>;

let _eventHandlers: EventHandlersMap = {};
let timeoutID: ReturnType<typeof setTimeout> | null = null;

interface UseKeyboardNavigationProps {
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  images: unknown[];
}

export const useKeyboardNavigation = ({ selectedIndex, setSelectedIndex, images }: UseKeyboardNavigationProps) => {
  useEffect(() => {
    if (!isWeb) return;

    const scrollTo = (index: number) => setSelectedIndex(index);

    const press = (e: KeyboardEvent) => {
      e.preventDefault();

      if (timeoutID) clearTimeout(timeoutID);

      timeoutID = setTimeout(() => {
        const keyName = e.key;
        const last = images.length - 1;

        switch (keyName) {
          case 'ArrowRight':
            selectedIndex > 0 ? scrollTo(selectedIndex - 1) : scrollTo(0);
            break;
          case 'ArrowLeft':
            selectedIndex < last ? scrollTo(selectedIndex + 1) : scrollTo(last);
            break;
        }
      }, 200);
    };

    const addListener = (node: Document, event: keyof DocumentEventMap, handler: (event: KeyboardEvent) => void, capture = false) => {
      const wrappedHandler = (e: Event) => handler(e as KeyboardEvent);

      if (!_eventHandlers[event]) {
        _eventHandlers[event] = [];
      }

      _eventHandlers[event].push({ node, handler: wrappedHandler, capture });
      node.addEventListener(event, wrappedHandler, capture);
    };

    const removeAllListeners = (targetNode: Document, event: keyof DocumentEventMap) => {
      if (!_eventHandlers[event]) return;

      _eventHandlers[event]
        .filter(({ node }) => node === targetNode)
        .forEach(({ node, handler, capture }) => node.removeEventListener(event, handler, capture));

      _eventHandlers[event] = _eventHandlers[event].filter(({ node }) => node !== targetNode);
    };

    addListener(document, 'keydown', press, false);

    return () => {
      removeAllListeners(document, 'keydown');
    };
  }, [isWeb, selectedIndex, images, setSelectedIndex]);
};
